// src/pages/SignupPage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import Seo from './Seo';
import './AuthForm.css';
import API_BASE_URL from '../config';
import { initializeGoogleSignIn, renderGoogleButton } from '../services/googleAuthService';

const BACKEND_URL = API_BASE_URL;

function SignupPage({ setIsAuthenticated }) {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Initialize Google Sign-In
  useEffect(() => {
    const handleGoogleSuccess = (data) => {
      setIsAuthenticated(true);
      navigate('/dashboard');
    };

    const handleGoogleError = (error) => {
      alert(error || 'Google sign-in failed');
    };

    // Wait for Google SDK to load
    const initGoogle = () => {
      if (window.google) {
        initializeGoogleSignIn(handleGoogleSuccess, handleGoogleError);
        renderGoogleButton('googleSignUpButton', {
          theme: 'outline',
          size: 'large',
          text: 'signup_with',
        });
      } else {
        // Retry after a short delay
        setTimeout(initGoogle, 100);
      }
    };

    initGoogle();
  }, [navigate, setIsAuthenticated]);

  const handleSignup = async () => {
    if (!username || !password || !confirmPassword) {
      alert('Please fill in all required fields');
      return;
    }

    if (password !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/signup/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          password,
          confirm_password: confirmPassword,
          email: email || undefined
        }),
      });

      const data = await response.json();

      if (response.ok && data.access_token && data.refresh_token) {
        localStorage.setItem('accessToken', data.access_token);
        localStorage.setItem('refreshToken', data.refresh_token);
        localStorage.setItem('userEmail', email || username);
        setIsAuthenticated(true);
        navigate('/dashboard');
      } else {
        alert(data.error || 'Signup failed. Try a different username.');
      }
    } catch (error) {
      console.error('Signup error:', error);
      alert('Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-wrapper">
      <Seo path="/signup" title="Sign up — Tudu" description="Create a free Tudu account and start planning tasks, routines, trips, and saving with local deals." noindex />
      <div className="auth-container">
        <h2 className="auth-title">Sign Up</h2>

        {/* Google Sign-Up Button */}
        <div id="googleSignUpButton" style={{
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'center'
        }}></div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          margin: '20px 0',
          color: '#666'
        }}>
          <div style={{ flex: 1, height: '1px', background: '#ddd' }}></div>
          <span style={{ padding: '0 15px', fontSize: '14px' }}>or</span>
          <div style={{ flex: 1, height: '1px', background: '#ddd' }}></div>
        </div>

        <input
          className="auth-input"
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <input
          className="auth-input"
          type="email"
          placeholder="Email (optional)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <div style={{ position: 'relative', marginBottom: '1.2rem' }}>
          <input
            className="auth-input"
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ marginBottom: 0, paddingRight: '48px' }}
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', color: '#888', display: 'inline-flex', alignItems: 'center', padding: '4px' }}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
        <div style={{ position: 'relative', marginBottom: '1.2rem' }}>
          <input
            className="auth-input"
            type={showConfirm ? 'text' : 'password'}
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={{ marginBottom: 0, paddingRight: '48px' }}
          />
          <button
            type="button"
            onClick={() => setShowConfirm((s) => !s)}
            aria-label={showConfirm ? 'Hide password' : 'Show password'}
            style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', color: '#888', display: 'inline-flex', alignItems: 'center', padding: '4px' }}
          >
            {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
        <button className="auth-btn" onClick={handleSignup} disabled={loading}>
          {loading ? 'Creating...' : 'Create Account'}
        </button>
        <div className="auth-links">
          <span>Already have an account? </span>
          <Link to="/login">Login</Link>
        </div>
      </div>
    </div>
  );
}

export default SignupPage;
